/**
 * Провайдер национальных оплат UZ (Click / Payme) для порта ядра.
 *
 * Ядро не знает про UzPayPlugin: в bootstrap регистрируется провайдер,
 * UI запрашивает QR по способу оплаты `uz-click` / `uz-payme`.
 */
import { PAYMENT_METHODS } from '@core/payments/types'
import type { PaymentProvider, PaymentQr, PaymentQrRequest } from '@core/payments/types'
import type { UzPayConfig, UzPaymentProvider } from './types'
import { UzPayPlugin } from './index'

export interface UzPayProviderConfig {
  merchantId: string
  /** Только для Click. */
  serviceId?: string
}

/** Способ оплаты → провайдер Click/Payme. */
function providerFromMethod(method: string): UzPaymentProvider | null {
  if (method === PAYMENT_METHODS.uzClick) return 'click'
  if (method === PAYMENT_METHODS.uzPayme) return 'payme'
  return null
}

export function createUzPayProvider(config: UzPayProviderConfig): PaymentProvider {
  return {
    id: 'uz-pay',

    supports(method: string): boolean {
      return providerFromMethod(method) !== null
    },

    async createQr(req: PaymentQrRequest): Promise<PaymentQr> {
      const provider = providerFromMethod(req.method)
      if (!provider) {
        throw new Error(`Способ оплаты "${req.method}" не поддерживается`)
      }

      const uzConfig: UzPayConfig = {
        provider,
        merchantId: config.merchantId,
        serviceId: provider === 'click' ? config.serviceId : undefined
      }
      const plugin = new UzPayPlugin(uzConfig)
      const result = await plugin.createPayment(req.amount, req.receiptId)

      return {
        providerId: 'uz-pay',
        // Идентификатор операции, а не id чека: receiptId уходит в QR как order_id.
        operationId: `${provider}-${Date.now().toString(36)}`,
        payload: result.deepLink,
        svg: result.qrSvg
      }
    }
  }
}
